<template>
  <div class="promocodes-table">
    <div class="d-flex flex-wrap mb-3" style="gap: 10px;">
      <UniversalSelect
        v-model="filters.is_used"
        style="width: 180px;"
        :options="statusOptions"
        placeholder="Status"
        title="Status"
        :showSearch="true"
        :disabled="loading"
        @change="fetchPromocodesList"
      />
      <div class="d-flex align-items-end" style="height: 62px;">
        <SearchField
          :value="filters.search"
          :loading="loading"
          :disabled="loading"
          @search="changeSearchValue"
        />
      </div>
      <div
        v-if="isFiltered"
        style="width: fit-content; flex: 1 1 auto;"
        class="d-flex justify-content-end align-items-end"
      >
        <a-button
          size="large"
          @click="resetAllFilters"
        >
          Reset filters
        </a-button>
      </div>
    </div>
    <div class="position-relative">
      <a-table
        class="fortune-wheel-promocodes-table"
        :dataSource="data"
        :pagination="meta"
        :row-key="(record) => record.id"
        :customRow="customRow"
        :columns="columns"
        :loading="loading"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
        @change="changePagination"
      >
        <template slot="code" slot-scope="code, record">
          <span :class="{'text-default': record.is_used}">
            {{ code }}
          </span>
        </template>
        <template slot="is_used" slot-scope="is_used">
          <a-badge
            :count="is_used ? 'Used' : 'Unused'"
            :number-style="{
              backgroundColor: is_used ? '#ccc' : '#28a745',
              color: '#fff',
            }"
            style="transform: scale(1.2);"
          />
        </template>
      </a-table>
      <div
        v-if="selectedRowKeys.length"
        class="promocodes-table__delete-promocodes d-flex align-items-center cursor-pointer"
        @click="toggleShowDeleteDialog(true)"
      >
        <a-icon type="delete" class="text-danger font-size-18" />
        <span class="text-danger ml-2 d-none d-xl-block">Delete {{ selectedRowKeys.length }} selected promo codes</span>
      </div>
    </div>
    <DeleteDialog
      v-if="showDeleteDialog"
      :show="showDeleteDialog"
      title="Delete promo codes"
      :text="`You are about to delete <b>${selectedRowKeys.length} promo codes</b>. <br>This action is permanent and cannot be undone.`"
      :loading="loadingDeletePromocodes"
      @confirm="deleteSelectedPromocodes"
      @close="toggleShowDeleteDialog"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import SearchField from '@/components/custom/form/SearchField.vue'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import DeleteDialog from '@/components/custom/modals/DeleteDialog.vue'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getFilledFilters } from '@/composables/useGetFilledFilters.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  props: {
    editPermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SearchField,
    UniversalSelect,
    DeleteDialog,
  },
  data: () => ({
    categoryId: null,
    data: [],
    meta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    selectedRowKeys: [],
    loadingDeletePromocodes: false,
    loading: false,
    filters: {
      page: undefined,
      per_page: undefined,
      search: '',
      is_used: undefined,
    },
    statusOptions: [
      { name: 'Used', value: '1' },
      { name: 'Unused', value: '0' },
    ],
    columns: [
      {
        title: 'Promocode',
        dataIndex: 'code',
        width: '80%',
        ellipsis: true,
        scopedSlots: { customRender: 'code' },
      },
      {
        title: 'Status',
        dataIndex: 'is_used',
        width: '15%',
        scopedSlots: { customRender: 'is_used' },
      },
    ],
    showDeleteDialog: false,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isHasListItems() {
      return !!this.data?.length
    },
    isFiltered() {
      return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '')
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
  },
  methods: {
    customRow(promocode) {
      return {
        on: {
          click: () => {
            const id = promocode?.id
            if (!id) {
              return false
            }
            const idx = this.selectedRowKeys.indexOf(id)

            if (idx === -1) {
              this.selectedRowKeys.push(id)
              return false
            }
            this.selectedRowKeys.splice(idx, 1)
          },
        },
      }
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
    },
    toggleShowDeleteDialog(status = false) {
      this.showDeleteDialog = status
    },
    changeSearchValue(value) {
      this.filters.search = value
      this.fetchPromocodesList()
    },
    async changePagination(pagination) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize

      this.fetchPromocodesList()
    },
    getFilters() {
      setQueryParams(this.filters, this)
      if (!this.isFiltered) {
        return {}
      }

      return getFilledFilters({ filters: this.filters })
    },
    async fetchPromocodesList() {
      this.loading = true

      const filters = this.getFilters()

      const url = `${wheelApiUrl}/admin/promocodes/categories/${this.categoryId}/get-promocodes`

      try {
        const response = await apiClient.get(url, { params: filters })

        if (response?.data?.status) {
          this.data = response?.data?.data || []
          this.meta = getTableMeta(response?.data?.meta)
          return
        }
        this.$notification.error({
          message: 'Error when trying to get Wheel of Fortune promo codes',
        })
      } catch (e) {
        console.log('Error when trying to get Wheel of Fortune promo codes', e)
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    async deleteSelectedPromocodes() {
      try {
        this.loadingDeletePromocodes = true

        const url = `${wheelApiUrl}/admin/promocodes/categories/${this.categoryId}/promocodes-batch`

        const response = await apiClient.delete(url, { params: { ids: this.selectedRowKeys } })
        if (response?.data?.status) {
          this.$emit('update')
          this.fetchPromocodesList()
          this.onSelectChange([])
          this.toggleShowDeleteDialog()
          return
        }
        this.$notification.error({
          message: 'An error occurred while deleting promo codes',
        })
      } catch (e) {
        console.log('An error occurred while deleting promo codes.', e)
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loadingDeletePromocodes = false
      }
    },
    resetAllFilters() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.fetchPromocodesList()
    },
  },
  async mounted() {
    this.categoryId = this.$route?.params?.id
    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this)
    }
    this.fetchPromocodesList()
  },
}
</script>

<style lang="scss">
.promocodes-table {
  padding: 24px;
  background-color: #fff;
  height: 100%;
  &__delete-promocodes {
    position: absolute;
    bottom: 0;
    left: 0;
  }
  .ant-checkbox-inner {
    border: 1px dashed #1890ff;
  }
  .ant-checkbox-checked .ant-checkbox-inner {
    border-style: solid;
  }
}
[data-kit-theme="default"] .ant-table-empty .ant-table-body {
  overflow: visible !important;
}
.ant-table-column-has-actions {
  overflow: visible !important;
}
</style>
